// Zocdoc libraries
import {
    Calendar,
    Clock,
    Close,
    Insurance,
    MedicalTeam,
    Settings,
    Signout,
    WellGuide,
    Zee,
} from '@zocdoc-frontend-common/assets-icons';
import { DropdownLinks } from '@zocdoc-frontend-common/utils-constants/HeaderLinks';

const includeSubpatientInDropdown = (useSubPatientDropdownLink = false) => {
    return useSubPatientDropdownLink
        ? [
              {
                  message: 'My dependents',
                  url: DropdownLinks.subPatients,
                  icon: MedicalTeam,
                  iconHeight: 14,
                  dataTest: 'subpatients',
                  metrics: {
                      action: 'Click PageHeader Username Dropdown SubPatients',
                      element: 'SubPatients',
                  },
              },
          ]
        : [];
};

const DesktopPatientLinks = [
    {
        message: 'My medical team',
        url: DropdownLinks.medicalTeam,
        icon: MedicalTeam,
        dataTest: 'medical-team',
        metrics: {
            action: 'Click PageHeader Username Dropdown MedicalTeam',
            element: 'MedicalTeam',
        },
    },
    {
        message: 'Appointment history',
        url: DropdownLinks.appointmentHistory,
        icon: Clock,
        dataTest: 'appointment-history',
        metrics: {
            action: 'Click PageHeader Username Dropdown PastAppointments',
            element: 'PastAppointments',
        },
    },
    {
        message: 'Settings',
        url: DropdownLinks.settings,
        icon: Settings,
        dataTest: 'settings',
        metrics: {
            action: 'Click PageHeader Username Dropdown Settings',
            element: 'Settings',
        },
    },
    {
        message: 'Sign out',
        url: DropdownLinks.signout,
        icon: Close,
        iconHeight: 14,
        dataTest: 'sign-out',
        metrics: {
            action: 'Click PageHeader Username Dropdown SignOut',
            element: 'SignOut',
        },
    },
];

export const DesktopPatientWithNavigationLinks = useSubPatientDropdownLink => [
    {
        message: 'Settings',
        url: DropdownLinks.settings,
        icon: Settings,
        dataTest: 'settings',
        metrics: {
            action: 'Click PageHeader Username Dropdown Settings',
            element: 'Settings',
        },
    },
    ...includeSubpatientInDropdown(useSubPatientDropdownLink),
    {
        message: 'Sign out',
        url: DropdownLinks.signout,
        icon: Close,
        iconHeight: 14,
        dataTest: 'sign-out',
        metrics: {
            action: 'Click PageHeader Username Dropdown SignOut',
            element: 'SignOut',
        },
    },
];

const MobilePatientLinks = usePatientHomeView => {
    const links = [
        {
            message: 'My Zocdoc',
            url: DropdownLinks.medicalTeam,
            icon: props => Zee({ expression: Zee.expression.smile, ...props }),
            dataTest: 'my-zocdoc',
        },
        {
            message: usePatientHomeView ? 'My Zocdoc' : 'My doctors',
            url: usePatientHomeView
                ? DropdownLinks.medicalTeam
                : DropdownLinks.myDoctors,
            icon: MedicalTeam,
            dataTest: usePatientHomeView ? 'medical-team' : 'my-doctors',
        },
        {
            message: 'Well guide',
            url: DropdownLinks.myWellGuide,
            icon: WellGuide,
            dataTest: 'well-guide',
        },
        {
            message: 'Upcoming appointments',
            url: DropdownLinks.upcomingAppointments,
            icon: Clock,
            dataTest: 'upcoming-appointments',
        },
        {
            message: 'Appointment history',
            url: DropdownLinks.appointmentHistory,
            icon: Calendar,
            dataTest: 'appointment-history',
        },
        {
            message: 'Insurance',
            url: DropdownLinks.insurance,
            icon: Insurance,
            dataTest: 'insurance',
        },
        {
            message: 'Settings',
            url: DropdownLinks.settings,
            icon: Settings,
            dataTest: 'settings',
        },
        {
            message: 'Sign out',
            url: DropdownLinks.signout,
            icon: Signout,
            dataTest: 'sign-out',
        },
    ];

    return links;
};

const ProviderLinks = [
    {
        message: 'Calendar',
        url: DropdownLinks.providerCalendar,
        dataTest: 'provider-calendar',
        metrics: {
            action: 'Click PageHeader Username Dropdown Calendar',
            element: 'Calendar',
        },
    },
    {
        message: 'Help',
        url: DropdownLinks.providerHelp,
        dataTest: 'provider-help',
        metrics: {
            action: 'Click PageHeader Username Dropdown Help',
            element: 'Help',
        },
    },
    {
        message: 'Sign out',
        url: DropdownLinks.signout,
        dataTest: 'sign-out',
        metrics: {
            action: 'Click PageHeader Username Dropdown SignOut',
            element: 'SignOut',
        },
    },
];

const ZdEmployeeLinks = [
    {
        message: 'Zocdoc Pulse',
        url: DropdownLinks.pulse,
        dataTest: 'pulse',
    },
    {
        message: 'Sign out',
        url: DropdownLinks.signout,
        dataTest: 'sign-out',
    },
];

export const DesktopUserLinks = {
    Patient: DesktopPatientLinks,
    Provider: ProviderLinks,
    ZdEmployee: ZdEmployeeLinks,
};

export const MobileUserLinks = usePatientHomeView => ({
    Patient: MobilePatientLinks(usePatientHomeView),
    Provider: ProviderLinks,
    ZdEmployee: ZdEmployeeLinks,
});

const DesktopPatientNavLinks = [
    {
        message: 'My Zocdoc',
        url: DropdownLinks.medicalTeam,
        dataTest: 'my-zocdoc',
        icon: props => Zee({ expression: Zee.expression.smile, ...props }),
    },
    {
        message: 'Well Guide',
        url: DropdownLinks.myWellGuide,
        dataTest: 'my-well-guide',
        icon: WellGuide,
    },
    {
        message: 'Past appointments',
        url: DropdownLinks.appointmentHistory,
        dataTest: 'past-appointments',
        icon: Clock,
    },
];

const DesktopProviderNavLinks = [
    {
        message: 'Calendar',
        url: DropdownLinks.providerCalendar,
        dataTest: 'calendar',
    },
    {
        message: 'Account',
        url: DropdownLinks.providerAccount,
        dataTest: 'account',
    },
];

export const DesktopUserNavLinks = {
    patient: DesktopPatientNavLinks,
    Provider: DesktopProviderNavLinks,
};
